import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutlined';
import GroupIcon from '@mui/icons-material/Group';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import AssignmentIcon from '@mui/icons-material/Assignment';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import StopIcon from '@mui/icons-material/Stop';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../../auth/useAuth';
import { useRunState } from '../../hooks/useRunState';
import { useWebSocket } from '../../hooks/useWebSocket';
import {
  listTeams,
  listFeed,
  getFeedbackSummary,
  listFeedback,
  getTeamProgress,
} from '../../api/runs';
import { ApiError } from '../../api/client';
import type {
  FeedEvent,
  FeedbackSummary,
  Feedback,
  RunState,
  Team,
  TeamProgressEntry,
} from '../../api/types';
import ConfirmDialog from '../../components/ConfirmDialog';
import TeamMembersDialog from '../../components/TeamMembersDialog';
import ErrorAlert from '../../components/ErrorAlert';
import LoadingSpinner from '../../components/LoadingSpinner';
import FeedComposer from '../../components/FeedComposer';
import { feedMessage } from '../../utils/feedMessage';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';

const STATE_COLORS: Record<
  RunState,
  'default' | 'primary' | 'success' | 'warning' | 'error'
> = {
  configured: 'default',
  scheduled: 'primary',
  active: 'success',
  paused: 'warning',
  ended: 'error',
};

// Detects a coordinator-help request from a SYSTEM feed event content payload.
// Preferred: backend includes a team_id field. Falls back to parsing the
// localized message ("Team X benötigt Hilfe." / "Team X needs help.") for
// older events that pre-date the team_id field.
function extractCoordinatorRequest(
  content: unknown,
): { teamId?: string; teamName?: string } | null {
  if (!content || typeof content !== 'object') return null;
  const c = content as Record<string, unknown>;
  if (typeof c.team_id === 'string' && c.team_id) {
    return {
      teamId: c.team_id,
      teamName: typeof c.team_name === 'string' && c.team_name ? c.team_name : undefined,
    };
  }
  for (const raw of [c.de, c.en]) {
    if (typeof raw !== 'string') continue;
    const m =
      raw.match(/^Team (.+?) ben[öo]tigt Hilfe\.?$/i) ??
      raw.match(/^Team (.+?) needs help\.?$/i);
    if (m) return { teamName: m[1].trim() };
  }
  return null;
}

function formatDuration(ms: number, hoursUnit: string, minutesUnit: string): string {
  const totalMinutes = Math.max(0, Math.floor(ms / 60_000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours} ${hoursUnit} ${minutes} ${minutesUnit}`;
}

const RunDashboard: React.FC = () => {
  const { t, i18n } = useTranslation('runs');
  const { t: tAdmin } = useTranslation('admin');
  const { user } = useAuth();
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();

  const orgSlug = user?.organization_slug ?? '';

  const { run, state, wsStatus, loading, error, start, pause, resume, end, schedule } =
    useRunState({ orgSlug, runId });

  // Teams
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [teamProgress, setTeamProgress] = useState<TeamProgressEntry[]>([]);

  // Feed
  const [feedEvents, setFeedEvents] = useState<FeedEvent[]>([]);

  // Teams that have requested coordinator assistance (highlighted in the team list).
  const [requestingTeamIds, setRequestingTeamIds] = useState<Set<string>>(new Set());
  // Team whose member list is open in the manage-members dialog (#252)
  const [membersTeam, setMembersTeam] = useState<{ id: string; name: string } | null>(null);
  const [coordinatorRequestToast, setCoordinatorRequestToast] = useState<string | null>(null);
  const [copyToast, setCopyToast] = useState<string | null>(null);

  // Feedback summary (ended runs only)
  const [feedbackSummary, setFeedbackSummary] = useState<FeedbackSummary | null>(null);
  const [feedbackItems, setFeedbackItems] = useState<Feedback[]>([]);

  // End confirm dialog
  const [endConfirmOpen, setEndConfirmOpen] = useState(false);
  const [endLoading, setEndLoading] = useState(false);

  // Schedule picker
  const [schedulePickerOpen, setSchedulePickerOpen] = useState(false);
  const [scheduleDateTime, setScheduleDateTime] = useState('');
  const [scheduleLoading, setScheduleLoading] = useState(false);

  const [actionError, setActionError] = useState<string | null>(null);

  // Live "since start" clock, ticked once a minute.
  const [now, setNow] = useState<number>(() => Date.now());
  // Join info is collapsible. Open while the run is fresh; auto-collapsed when
  // the dashboard is first opened >= 15 min after start (the join code matters
  // mostly at the beginning). The user can still toggle it either way.
  const [joinOpen, setJoinOpen] = useState(true);
  const joinAutoCollapsed = useRef(false);

  const loadTeams = useCallback(async ({ quiet = false }: { quiet?: boolean } = {}) => {
    if (!orgSlug || !runId) return;
    if (!quiet) setTeamsLoading(true);
    try {
      const data = await listTeams(orgSlug, runId);
      setTeams(data.items);
    } catch {
      // non-fatal
    } finally {
      setTeamsLoading(false);
    }
    // Fetch team progress in parallel; failures are silent (the row
    // falls back to the basic "{n} members" caption).
    void getTeamProgress(orgSlug, runId)
      .then((p) => {
        setTeamProgress(p.teams);
        // Seed the help-highlight from progress so a team that asked for help
        // before the dashboard was opened shows up immediately (not only on a
        // later live WS event).
        setRequestingTeamIds((prev) => {
          const next = new Set(prev);
          p.teams.forEach((tp) => {
            if (tp.requested_help) next.add(tp.team_id);
          });
          return next;
        });
      })
      .catch(() => setTeamProgress([]));
  }, [orgSlug, runId]);

  const loadFeed = useCallback(async () => {
    if (!orgSlug || !runId) return;
    try {
      const data = await listFeed(orgSlug, runId);
      setFeedEvents(data.items);
    } catch {
      // non-fatal
    }
  }, [orgSlug, runId]);

  const loadFeedback = useCallback(async () => {
    if (!orgSlug || !runId) return;
    try {
      const [summary, items] = await Promise.all([
        getFeedbackSummary(orgSlug, runId),
        listFeedback(orgSlug, runId),
      ]);
      setFeedbackSummary(summary);
      setFeedbackItems(items.items);
    } catch {
      // non-fatal
    }
  }, [orgSlug, runId]);

  useEffect(() => {
    void loadTeams();
    void loadFeed();
  }, [loadTeams, loadFeed]);

  // Teams form and grow while this dashboard sits open; only a help request
  // used to refetch them, so a coordinator watching teams join saw none.
  useAutoRefresh(() => loadTeams({ quiet: true }));

  useEffect(() => {
    if (state === 'ended') void loadFeedback();
  }, [state, loadFeedback]);

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (joinAutoCollapsed.current || !run?.started_at) return;
    joinAutoCollapsed.current = true;
    if (Date.now() - Date.parse(run.started_at) >= 15 * 60_000) setJoinOpen(false);
  }, [run?.started_at]);

  // Subscribe to WS for live feed events (separate from useRunState's WS)
  useWebSocket({
    path: orgSlug && runId ? `/api/orgs/${orgSlug}/runs/${runId}/ws` : '',
    enabled: !!orgSlug && !!runId,
    handlers: {
      feed_event: (data) => {
        const event = data as FeedEvent;
        setFeedEvents((prev) =>
          prev.some((e) => e.id === event.id) ? prev : [event, ...prev],
        );
        if (event.event_type === 'system') {
          const req = extractCoordinatorRequest(event.content);
          if (req) {
            setTeams((prevTeams) => {
              const match = req.teamId
                ? prevTeams.find((t) => t.id === req.teamId)
                : prevTeams.find((t) => t.name === req.teamName);
              const highlightId = req.teamId ?? match?.id;
              if (highlightId) {
                setRequestingTeamIds((prev) => {
                  const next = new Set(prev);
                  next.add(highlightId);
                  return next;
                });
              }
              const toastName = match?.name ?? req.teamName;
              if (toastName) {
                setCoordinatorRequestToast(
                  tAdmin('coordinatorRequest.teamRequestedHelp', { teamName: toastName }),
                );
              }
              // Refresh the team list: a request opens the team's coordinator
              // assist window (spec 037), so coordinator_chat_open must be
              // refetched — and a not-yet-listed team needs its row rendered.
              void loadTeams();
              return prevTeams;
            });
          }
        }
      },
    },
  });

  // Open the coordinator chat for a team. Clears the local "needs help"
  // highlight for that team — there is no backend "resolved" state for help
  // requests, so opening the chat counts as the coordinator's acknowledgment
  // (client-side only; a new live request re-adds the highlight).
  const handleOpenTeamChat = (teamId: string) => {
    setRequestingTeamIds((prev) => {
      if (!prev.has(teamId)) return prev;
      const next = new Set(prev);
      next.delete(teamId);
      return next;
    });
    navigate(`/runs/${runId}/teams/${teamId}/chat`);
  };

  const handleCopyCode = () => {
    if (run?.join_code) {
      void navigator.clipboard
        .writeText(run.join_code)
        .then(() => setCopyToast(t('dashboard.copyCodeSuccess')))
        .catch(() => setCopyToast(t('dashboard.copyCodeFailure')));
    }
  };

  const handleEnd = async () => {
    setEndLoading(true);
    setActionError(null);
    try {
      await end();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.detail : String(err));
    } finally {
      setEndLoading(false);
      setEndConfirmOpen(false);
    }
  };

  const handleSchedule = async () => {
    if (!scheduleDateTime) return;
    setScheduleLoading(true);
    setActionError(null);
    try {
      await schedule(new Date(scheduleDateTime).toISOString());
      setSchedulePickerOpen(false);
      setScheduleDateTime('');
    } catch (err) {
      setActionError(err instanceof ApiError ? err.detail : String(err));
    } finally {
      setScheduleLoading(false);
    }
  };

  const handleStart = async () => {
    setActionError(null);
    try {
      await start();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.detail : String(err));
    }
  };

  const handlePause = async () => {
    setActionError(null);
    try {
      await pause();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.detail : String(err));
    }
  };

  const handleResume = async () => {
    setActionError(null);
    try {
      await resume();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.detail : String(err));
    }
  };

  if (loading && !run) return <LoadingSpinner />;
  if (error && !run) return <ErrorAlert message={error} />;
  if (!run) return null;

  const joinUrl = `${window.location.origin}/join/${run.join_code}`;
  const maxDist = feedbackSummary
    ? Math.max(...Object.values(feedbackSummary.distribution), 1)
    : 1;

  const startedMs = run.started_at ? Date.parse(run.started_at) : null;
  const endedMs = run.ended_at ? Date.parse(run.ended_at) : null;
  const elapsed =
    startedMs && (endedMs ?? now) > startedMs
      ? formatDuration(
          (endedMs ?? now) - startedMs,
          t('dashboard.metrics.hoursUnit'),
          t('dashboard.metrics.minutesUnit'),
        )
      : t('dashboard.metrics.notStarted');
  const submittedCount = teamProgress.reduce((n, p) => n + p.challenges_submitted, 0);
  const scoredCount = teamProgress.reduce((n, p) => n + p.challenges_scored, 0);
  const newCount = Math.max(0, submittedCount - scoredCount);

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', mb: 2 }}>
        <Typography variant="h2" component="h1">
          {run.name}
        </Typography>
        {wsStatus !== 'open' && (
          <Tooltip title={`ws: ${wsStatus}`}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: wsStatus === 'connecting' ? 'warning.main' : 'error.main',
              }}
            />
          </Tooltip>
        )}
      </Stack>

      {(error || actionError) && (
        <Box sx={{ mb: 2 }}>
          <ErrorAlert message={error ?? actionError ?? ''} />
        </Box>
      )}

      {/* Status: state + elapsed + run controls, with the key metrics */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' } }}
          >
            <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
              {state && (
                <Chip
                  label={t(`dashboard.state.${state}`)}
                  color={STATE_COLORS[state]}
                  size="small"
                />
              )}
              {startedMs && (
                <Typography variant="body2" color="text.secondary">
                  {t('dashboard.metrics.timeSinceStart')}{' '}
                  <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    {elapsed}
                  </Box>
                </Typography>
              )}
            </Stack>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
              {(state === 'configured' || state === 'scheduled') && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<PlayArrowIcon />}
                  onClick={() => void handleStart()}
                >
                  {t('dashboard.actions.start')}
                </Button>
              )}
              {state === 'configured' && (
                <Button
                  variant="outlined"
                  startIcon={<ScheduleIcon />}
                  onClick={() => setSchedulePickerOpen((v) => !v)}
                >
                  {t('dashboard.actions.schedule')}
                </Button>
              )}
              {state === 'active' && (
                <Button
                  variant="outlined"
                  startIcon={<PauseIcon />}
                  onClick={() => void handlePause()}
                >
                  {t('dashboard.actions.pause')}
                </Button>
              )}
              {state === 'paused' && (
                <Button
                  variant="contained"
                  startIcon={<PlayArrowIcon />}
                  onClick={() => void handleResume()}
                >
                  {t('dashboard.actions.resume')}
                </Button>
              )}
              {(state === 'active' || state === 'paused') && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<StopIcon />}
                  onClick={() => setEndConfirmOpen(true)}
                >
                  {t('dashboard.actions.end')}
                </Button>
              )}
            </Stack>
          </Stack>

          {schedulePickerOpen && (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mt: 2, flexWrap: 'wrap' }}>
              <TextField
                label={t('dashboard.actions.scheduleDateLabel')}
                type="datetime-local"
                value={scheduleDateTime}
                onChange={(e) => setScheduleDateTime(e.target.value)}
                size="small"
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <Button
                variant="contained"
                size="small"
                onClick={() => void handleSchedule()}
                disabled={!scheduleDateTime || scheduleLoading}
              >
                OK
              </Button>
              <Button size="small" onClick={() => setSchedulePickerOpen(false)}>
                {t('actions.cancel', { ns: 'common' })}
              </Button>
            </Stack>
          )}

          <Divider sx={{ my: 2 }} />

          <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap', rowGap: 2, alignItems: 'center' }}>
            {/* Teams */}
            <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
              <GroupIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {t('dashboard.metrics.teams')}
                </Typography>
                <Typography variant="h6" component="p" sx={{ fontWeight: 700, lineHeight: 1 }}>
                  {teams.length}
                </Typography>
              </Box>
            </Stack>

            {/* Submissions — clickable, with rated / new split */}
            <Box
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/runs/${runId}/submissions`)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') navigate(`/runs/${runId}/submissions`);
              }}
              sx={{
                display: 'flex',
                gap: 1.25,
                alignItems: 'center',
                cursor: 'pointer',
                borderRadius: 1,
                p: 1,
                m: -1,
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <AssignmentIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {t('submissions.title')}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                  <Typography variant="h6" component="p" sx={{ fontWeight: 700, lineHeight: 1 }}>
                    {scoredCount}{' '}
                    <Box
                      component="span"
                      sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary' }}
                    >
                      {t('dashboard.metrics.scored')}
                    </Box>
                  </Typography>
                  {newCount > 0 && (
                    <Chip
                      label={`${newCount} ${t('dashboard.metrics.new')}`}
                      size="small"
                      sx={{
                        bgcolor: 'secondary.main',
                        color: 'secondary.contrastText',
                        fontWeight: 700,
                      }}
                    />
                  )}
                  <Typography variant="body2" color="primary" sx={{ fontWeight: 700 }}>
                    {t('dashboard.metrics.open')} →
                  </Typography>
                </Stack>
              </Box>
            </Box>

            {/* Scoreboard — clickable */}
            <Box
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/runs/${runId}/scoreboard`)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') navigate(`/runs/${runId}/scoreboard`);
              }}
              sx={{
                display: 'flex',
                gap: 1.25,
                alignItems: 'center',
                cursor: 'pointer',
                borderRadius: 1,
                p: 1,
                m: -1,
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <EmojiEventsIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {t('scoreboard.title')}
                </Typography>
                <Typography variant="body2" color="primary" sx={{ fontWeight: 700 }}>
                  {t('dashboard.metrics.open')} →
                </Typography>
              </Box>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Join info — collapsible (open while fresh, auto-collapsed >= 15 min after start) */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack
            direction="row"
            spacing={1}
            onClick={() => setJoinOpen((v) => !v)}
            sx={{ alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}
          >
            <Typography variant="h6" component="h2">
              {t('dashboard.joinCode')}
            </Typography>
            {!joinOpen && (
              <Typography
                variant="body2"
                sx={{ fontFamily: 'monospace', letterSpacing: 2, color: 'text.secondary' }}
              >
                {run.join_code}
              </Typography>
            )}
            <Box sx={{ flexGrow: 1 }} />
            <IconButton size="small" aria-label={t('dashboard.joinCode')}>
              {joinOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Stack>
          <Collapse in={joinOpen}>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start', mt: 2 }}>
              <Stack spacing={1} sx={{ minWidth: 240 }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 96 }}>
                    {t('dashboard.joinCode')}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: 'monospace', fontWeight: 700, letterSpacing: 1 }}
                  >
                    {run.join_code}
                  </Typography>
                  <IconButton size="small" onClick={handleCopyCode} aria-label={t('dashboard.copyCode')}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Stack>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 96 }}>
                    {t('dashboard.joinLink')}
                  </Typography>
                  <Typography
                    variant="body2"
                    component="a"
                    href={joinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: 'primary.main', wordBreak: 'break-all' }}
                  >
                    {joinUrl}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => void navigator.clipboard.writeText(joinUrl)}
                    aria-label={t('dashboard.copyJoinUrl')}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>
              <QRCodeSVG
                value={joinUrl}
                size={128}
                title={t('dashboard.qrTitle', { code: run.join_code })}
              />
            </Box>
          </Collapse>
        </CardContent>
      </Card>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 3,
        }}
      >
        {/* Teams */}
        <Card>
          <CardContent>
            <Typography variant="h6" component="h2" gutterBottom>
              {t('dashboard.teamsTitle')} ({teams.length})
            </Typography>
            {teamsLoading ? (
              <LoadingSpinner minHeight={60} />
            ) : teams.length === 0 ? (
              <Typography color="text.secondary" variant="body2">
                —
              </Typography>
            ) : (
              <List dense disablePadding>
                {teams.map((team) => {
                  const requesting = requestingTeamIds.has(team.id);
                  return (
                    <ListItem
                      key={team.id}
                      disablePadding
                      sx={
                        requesting
                          ? {
                              bgcolor: 'secondary.main',
                              borderRadius: 1,
                              px: 1,
                            }
                          : undefined
                      }
                      secondaryAction={
                        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                          <Tooltip title={t('teamMembers.manage', { teamName: team.name })}>
                            <IconButton
                              size="small"
                              aria-label={t('teamMembers.manage', { teamName: team.name })}
                              onClick={() => setMembersTeam({ id: team.id, name: team.name })}
                            >
                              <ManageAccountsIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {requesting && (
                            <Chip
                              icon={<SupportAgentIcon />}
                              label={tAdmin('coordinatorRequest.teamRequestedHelp', { teamName: team.name })}
                              size="small"
                              onClick={() => handleOpenTeamChat(team.id)}
                              sx={{
                                bgcolor: 'text.primary',
                                color: 'background.paper',
                                '&:hover': { bgcolor: 'text.primary' },
                                '& .MuiChip-icon': { color: 'background.paper' },
                              }}
                            />
                          )}
                          <Tooltip
                            title={
                              team.coordinator_chat_open || requesting
                                ? t('teamChat.openChat', { teamName: team.name })
                                : t('teamChat.chatLocked', { teamName: team.name })
                            }
                          >
                            {/* MUI disabled buttons swallow pointer events, so the
                                Tooltip needs a wrapping span to still receive hover
                                — otherwise a disabled icon gives no explanation. */}
                            <span>
                              {/* Labelled, not icon-only (#419, Toni T3-27): this is
                                  the primary action on a team row, and an unlabelled
                                  speech bubble does not say whether it opens a chat,
                                  writes a message, or shows a history. */}
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<ChatBubbleOutlineIcon fontSize="small" />}
                                // Spec 037: coordinator chat opens on the team's request only.
                                // A live help request ("requesting") opens the window before
                                // the team list refreshes, so it also enables the button.
                                disabled={!team.coordinator_chat_open && !requesting}
                                aria-label={
                                  team.coordinator_chat_open || requesting
                                    ? t('teamChat.openChat', { teamName: team.name })
                                    : t('teamChat.chatLocked', { teamName: team.name })
                                }
                                onClick={() => handleOpenTeamChat(team.id)}
                                sx={{ whiteSpace: 'nowrap' }}
                              >
                                {t('teamChat.openChatShort')}
                              </Button>
                            </span>
                          </Tooltip>
                        </Stack>
                      }
                    >
                      <ListItemText
                        primary={team.name}
                        secondary={(() => {
                          const progress = teamProgress.find(
                            (p) => p.team_id === team.id,
                          );
                          if (!progress) {
                            return t('dashboard.memberCount', { count: team.member_count });
                          }
                          return t('dashboard.teamProgressSummary', {
                            members: progress.member_count,
                            stationsDone: progress.stations_completed,
                            stationsTotal: progress.stations_total,
                            challengesDone: progress.challenges_submitted,
                            challengesTotal: progress.challenges_total,
                            points: progress.total_points,
                          });
                        })()}
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}
          </CardContent>
        </Card>

        {/* Live feed */}
        <Card>
          <CardContent>
            <Typography variant="h6" component="h2" gutterBottom>
              {t('dashboard.feedTitle')}
            </Typography>
            <FeedComposer
              orgSlug={orgSlug}
              runId={run.id}
              teams={teams}
              onPosted={(event) =>
                setFeedEvents((prev) =>
                  prev.some((e) => e.id === event.id) ? prev : [event, ...prev],
                )
              }
            />
            <Divider sx={{ my: 1 }} />
            {/* Without a heading the list below reads as part of the composer —
                as if it were previewing what you are about to send, rather than
                showing what was already sent. (#418, Toni T3-31) */}
            {/* component="p": MUI renders subtitle2 as <h6>, which would jump
                the heading order inside the card (axe heading-order). This is a
                list label, not a document heading. */}
            <Typography variant="subtitle2" component="p" color="text.secondary" sx={{ mb: 0.5 }}>
              {t('feedComposer.pastEvents')}
            </Typography>
            <Stack spacing={1} aria-live="polite" aria-atomic={false} sx={{ maxHeight: 300, overflowY: 'auto' }}>
              {feedEvents.length === 0 ? (
                <Typography color="text.secondary" variant="body2">
                  {t('participant.feed.empty')}
                </Typography>
              ) : (
                feedEvents.map((ev) => (
                  <Box key={ev.id}>
                    <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
                      {/* Raw event_type ("system" etc.) isn't meaningful to admins on
                          its own — drop the pill for system events, show the localized
                          type label (not the enum value) for the rest. */}
                      {ev.event_type === 'system' ? (
                        <span />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {t(`feedComposer.types.${ev.event_type}`)}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {new Date(ev.created_at).toLocaleTimeString()}
                      </Typography>
                    </Stack>
                    <Typography variant="body2">
                      {feedMessage(ev.content, i18n.language)}
                    </Typography>
                  </Box>
                ))
              )}
            </Stack>
          </CardContent>
        </Card>
      </Box>

      {/* Feedback summary (ended runs only) */}
      {state === 'ended' && feedbackSummary && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" component="h2" gutterBottom>
              {t('participant.feedback.title')}
            </Typography>
            <Typography variant="body1" gutterBottom>
              {t('participant.feedback.rating')}: {feedbackSummary.average_rating.toFixed(1)} / 5
              &nbsp;({feedbackSummary.total_count})
            </Typography>
            <Stack spacing={0.5} sx={{ mb: 2 }}>
              {[5, 4, 3, 2, 1].map((star) => (
                <Stack key={star} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <Typography variant="caption" sx={{ width: 16 }}>
                    {star}
                  </Typography>
                  <Box sx={{ flexGrow: 1, bgcolor: 'grey.200', height: 12, borderRadius: 1 }}>
                    <Box
                      sx={{
                        width: `${((feedbackSummary.distribution[star] ?? 0) / maxDist) * 100}%`,
                        bgcolor: 'primary.main',
                        height: '100%',
                        borderRadius: 1,
                      }}
                    />
                  </Box>
                  <Typography variant="caption" sx={{ width: 24 }}>
                    {feedbackSummary.distribution[star] ?? 0}
                  </Typography>
                </Stack>
              ))}
            </Stack>
            <Stack spacing={0.5}>
              {feedbackItems
                .filter((f) => f.comment)
                .map((f) => (
                  <Typography key={f.id} variant="body2" sx={{ fontStyle: 'italic' }}>
                    "{f.comment}"
                  </Typography>
                ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Team member management (#252) — keyed by team so each team gets a
          fresh dialog state without in-effect resets (#351 lint rule) */}
      {runId && (
        <TeamMembersDialog
          key={membersTeam?.id ?? 'closed'}
          open={membersTeam !== null}
          onClose={() => setMembersTeam(null)}
          orgSlug={orgSlug}
          runId={runId}
          teamId={membersTeam?.id ?? null}
          teamName={membersTeam?.name ?? ''}
          rallyeCode={run?.join_code ?? ''}
          onMemberRemoved={() => void loadTeams()}
        />
      )}

      {/* End confirm dialog */}
      <ConfirmDialog
        open={endConfirmOpen}
        title={t('dashboard.actions.end')}
        message={t('dashboard.endConfirm')}
        confirmLabel={t('dashboard.actions.end')}
        destructive
        loading={endLoading}
        onConfirm={() => void handleEnd()}
        onCancel={() => setEndConfirmOpen(false)}
      />

      <Snackbar
        open={!!coordinatorRequestToast}
        autoHideDuration={6000}
        onClose={() => setCoordinatorRequestToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="warning"
          icon={<SupportAgentIcon />}
          onClose={() => setCoordinatorRequestToast(null)}
        >
          {coordinatorRequestToast}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!copyToast}
        autoHideDuration={3000}
        onClose={() => setCopyToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setCopyToast(null)}>
          {copyToast}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RunDashboard;
